// "What's new" — the window that lists the novelties of each portal release.
// Written by us at each release: add a new entry at the TOP with the same version as
// REACT_APP_VERSION in .env.production / .env.development. Only user-visible novelties
// (no technical improvements). The window opens once per user after a release.
//
// Item fields
//   title, text, where: { en, es, pt }   (a missing language falls back to English)
//   link (optional):
//     { module, page, tab }  a portal screen = PATH_DASHBOARD[module][page] (+ ?tab=);
//                            the item is shown only to users with access to that page
//     { help: true }         the help center, in the user's language (everyone)
//   image (optional): file under /public, e.g. '/assets/whats-new/1.2.0-sectors.png'

const RELEASE_NOTES = [
  {
    version: '1.2.0',
    items: [
      {
        title: {
          en: 'Help center',
          es: 'Centro de ayuda',
          pt: 'Centro de ajuda',
        },
        text: {
          en: 'A guide for every screen of the portal, with search. It opens in your language.',
          es: 'Una guía para cada pantalla del portal, con búsqueda. Se abre en su idioma.',
          pt: 'Um guia para cada ecrã do portal, com pesquisa. Abre no seu idioma.',
        },
        where: {
          en: 'Menu › Help',
          es: 'Menú › Ayuda',
          pt: 'Menu › Ajuda',
        },
        link: { help: true },
      },
      {
        title: {
          en: 'Totals in reports and in Excel',
          es: 'Totales en los informes y en Excel',
          pt: 'Totais nos relatórios e no Excel',
        },
        text: {
          en: 'Reports end with a Total row for the whole period, not only for the page on screen. The Excel export has the same row. Speed (kg/h) is always total kg ÷ total hours.',
          es: 'Los informes terminan con una fila Total de todo el período, no solo de la página visible. La exportación a Excel tiene la misma fila. La velocidad (kg/h) es siempre kg totales ÷ horas totales.',
          pt: 'Os relatórios terminam com uma linha Total de todo o período, não só da página visível. A exportação para Excel tem a mesma linha. A velocidade (kg/h) é sempre kg totais ÷ horas totais.',
        },
        where: {
          en: 'Harvest, Operations and Human Resources reports, e.g. Operations › Work Hours',
          es: 'Informes de Cosecha, Operaciones y Recursos Humanos, p. ej. Operaciones › Horas Trabajo',
          pt: 'Relatórios de Colheita, Operações e Recursos Humanos, p. ex. Operações › Horas Trabalho',
        },
        link: { module: 'operations', page: 'workHours' },
      },
      {
        title: {
          en: 'Productivity by supervisor, sector and company',
          es: 'Productividad por supervisor, sector y empresa',
          pt: 'Produtividade por supervisor, sector e empresa',
        },
        text: {
          en: 'Three new tabs compare teams, sectors and subcontractors: workers, hours, kg, kg/h and the difference to the farm average. The menu item "Picker Productivity" is now called Harvest Productivity.',
          es: 'Tres pestañas nuevas comparan equipos, sectores y subcontratas: trabajadores, horas, kg, kg/h y la diferencia con la media de la explotación. El menú "Picker Productivity" se llama ahora Productividad de la Cosecha.',
          pt: 'Três separadores novos comparam equipas, sectores e empresas subcontratadas: trabalhadores, horas, kg, kg/h e a diferença para a média da exploração. O menu "Picker Productivity" chama-se agora Produtividade da Colheita.',
        },
        where: {
          en: 'Harvest › Harvest Productivity › Supervisors / Sectors / Companies',
          es: 'Cosecha › Productividad de la Cosecha › Supervisores / Sectores / Empresas',
          pt: 'Colheita › Produtividade da Colheita › Supervisores / Sectores / Empresas',
        },
        link: { module: 'harvest', page: 'productivity', tab: 'location' },
      },
      {
        title: {
          en: 'Comparison with the previous day',
          es: 'Comparación con el día anterior',
          pt: 'Comparação com o dia anterior',
        },
        text: {
          en: 'New column "vs. previous day": how much each worker\'s kg/h went up or down compared with the day before.',
          es: 'Nueva columna "vs. día anterior": cuánto subió o bajó el kg/h de cada trabajador respecto al día anterior.',
          pt: 'Nova coluna "vs. dia anterior": quanto subiu ou desceu o kg/h de cada trabalhador face ao dia anterior.',
        },
        where: {
          en: 'Harvest › Harvest Productivity › By Sector',
          es: 'Cosecha › Productividad de la Cosecha › Por Sector',
          pt: 'Colheita › Produtividade da Colheita › Por Sector',
        },
        link: { module: 'harvest', page: 'productivity', tab: 'list' },
      },
      {
        title: {
          en: 'Sector trend',
          es: 'Evolución de los sectores',
          pt: 'Evolução dos sectores',
        },
        text: {
          en: 'A chart with the cumulative kg of up to 5 sectors over the period, by day or by week.',
          es: 'Gráfico con los kg acumulados de hasta 5 sectores a lo largo del período, por día o por semana.',
          pt: 'Gráfico com os kg acumulados de até 5 sectores ao longo do período, por dia ou por semana.',
        },
        where: {
          en: 'Harvest › Harvest Productivity › Trend',
          es: 'Cosecha › Productividad de la Cosecha › Evolución',
          pt: 'Colheita › Produtividade da Colheita › Evolução',
        },
        link: { module: 'harvest', page: 'productivity', tab: 'trend' },
      },
      {
        title: {
          en: 'Work maps without end time',
          es: 'Mapas de trabajo sin hora de fin',
          pt: 'Mapas de trabalho sem hora de fim',
        },
        text: {
          en: 'Instead of "-1" you now see a warning icon. Those rows are left out of hours and kg/h until the work map is closed.',
          es: 'En lugar de "-1" ahora aparece un icono de aviso. Esas filas no cuentan en las horas ni en el kg/h hasta que se cierre el mapa de trabajo.',
          pt: 'Em vez de "-1" aparece agora um ícone de aviso. Essas linhas não contam para as horas nem para o kg/h até o mapa de trabalho ser fechado.',
        },
        where: {
          en: 'Harvest › Harvest Productivity',
          es: 'Cosecha › Productividad de la Cosecha',
          pt: 'Colheita › Produtividade da Colheita',
        },
        link: { module: 'harvest', page: 'productivity', tab: 'list' },
      },
    ],
  },
];

export default RELEASE_NOTES;
